import { motion } from 'framer-motion';
import { CheckCheck, Pill, SearchCheck, ShieldCheckIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { StepIndicator } from './step-indicator';

interface TermsStepProps {
  onBack: () => void;
  onNext: () => void;
  currentStep: number;
  totalSteps: number;
}

export function TermsStep({
  onBack,
  onNext,
  currentStep,
  totalSteps,
}: TermsStepProps) {
  const { t } = useTranslation();

  const terms = [
    {
      icon: Pill,
      title: t('termsOfService:titleLine1'),
      description: t('termsOfService:summaryLine1'),
    },
    {
      icon: SearchCheck,
      title: t('termsOfService:titleLine2'),
      description: t('termsOfService:summaryLine2'),
    },
    {
      icon: ShieldCheckIcon,
      title: t('termsOfService:titleLine3'),
      description: t('termsOfService:summaryLine3'),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex h-full w-full max-w-xl flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight">
          {t('termsOfService:title')}
        </h1>
        <p className="text-muted-foreground">
          {t('termsOfService:description')}
        </p>
      </div>

      <div className="flex grow flex-col gap-6 overflow-y-auto">
        <div className="flex flex-col gap-6">
          {terms.map((term, index) => (
            <div key={index} className="flex items-center gap-4">
              <div className="bg-primary/10 text-primary flex h-12 w-12 shrink-0 items-center justify-center rounded-full">
                <term.icon className="h-6 w-6" />
              </div>
              <div className="flex flex-col gap-1">
                <h3 className="leading-none font-medium">{term.title}</h3>
                <p className="text-muted-foreground text-sm">
                  {term.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-muted-foreground text-sm">
          {t('termsOfService:description1')}
          <a
            className="text-primary hover:text-primary/80 underline"
            href={t('termsOfService:linkHref')}
            target="_blank"
            rel="noopener noreferrer">
            {t('termsOfService:linkText')}
          </a>
          {t('termsOfService:description2')}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onBack}>
            {t('login:back')}
          </Button>
          <Button className="flex-1 gap-2" onClick={onNext}>
            <CheckCheck className="h-4 w-4" />
            {t('termsOfService:acceptTerms')}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
